import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronRight, ExternalLink, History, Maximize2, Minimize2, Plus, Sparkles, X } from "lucide-react";
import type { GroomingRunnerEvent, GroomingStage } from "@ttc-spectree/toolbox-contract";
import { EngineerNotesBody, SizeDrivers, TextList } from "@/components/ado/grooming-brief-panel";
import { AdoReadErrorCard } from "@/components/grooming/ado-read-error-card";
import { AreaList } from "@/components/grooming/area-list";
import { BriefStateBar } from "@/components/grooming/brief-state-bar";
import { AuthorQuestionsCard } from "@/components/grooming/author-questions-card";
import { Chip, StageChip } from "@/components/grooming/grooming-chips";
import { NextInLineCard } from "@/components/grooming/next-in-line-card";
import { PointScale } from "@/components/grooming/point-scale";
import { PreparingMarker } from "@/components/grooming/preparing-marker";
import { QueueBadge, useEnqueueItem } from "@/components/grooming/queue-status-badge";
import { ReadinessChecklist, checklistRows } from "@/components/grooming/readiness-checklist";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { Button } from "@/components/ui/button";
import { useAddToAgenda, useProjectSession, useRemoveFromAgenda } from "@/hooks/queries/use-grooming";
import { useCan, useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { useAuthorQuestionsLatestAsk, useWorkItem, workItemKeys } from "@/hooks/queries/use-work-items";
import type { GroomingPreparing } from "@/lib/api/grooming";
import type {
  GroomingBrief,
  LatestAuthorQuestionAsk,
  ReadinessLevelResult,
  WorkItemDetail,
  WorkItemGroomingBriefBlock,
} from "@/lib/api/work-items";
import { firstName, shortDate } from "@/lib/grooming/ask-words";
import {
  confidenceTone,
  confidenceWords,
  groomCommand,
  rangeSentence,
  sizeSentence,
} from "@/lib/grooming/plain-words";
import { showsNextInLineCard } from "@/lib/grooming/next-in-line";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { PANE_WIDTH_DEFAULT, PANE_WIDTH_MIN, PANE_WIDTH_STEP } from "@/lib/grooming/pane-width";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The reading pane's line when an item has no brief (UX-PM-10). Nothing more:
 * who can prepare one is shown by the Groom this item action, which renders
 * only for people with `groom-one`. Exported for the gate-copy test.
 */
export const NO_BRIEF_TEXT = "No brief yet.";

/**
 * The backlog's reading pane (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1401;
 * Claude Design "SpecTree Grooming Desktop", Backlog with the pane open). It
 * replaces the E279 Item summary page: the same question headings (What is
 * it? Who notices? What gets touched? How big? Worth knowing, Ask the team)
 * with the engineer brief collapsed behind "Technical details for the team".
 * An item below the grooming standard shows the "N of M in place" checklist
 * and the questions for its author instead.
 *
 * Beside the List view (`side`) or over the Stages board (`over`), at the
 * width the Backlog remembers on this computer (540 px by default, E289,
 * UX-PM-18): its left edge is a resize handle (drag, or ←/→ when focused) and
 * the header's expand button shows it full width.
 * Under the header, the brief's state bar (E289, frame 1b) says whether the
 * brief is in ADO and, for people who may post, offers Hold back and Post to
 * ADO; it is the pane's only post control. For an item below the bar the same
 * slot says who wrote it and whether they were asked (E292, frames D3/D4),
 * and the questions for the author can be edited, posted with an @mention and
 * are remembered (AuthorQuestionsCard).
 * The brief comes from the item read's `groomingBrief` block (ER-1603 fix B):
 * one pane costs one live ADO read and never calls the latest-brief route.
 * Every string from the tracker or a model is rendered as React text only.
 */

export type ReadingPaneVariant = "side" | "over";

/** The grooming stage of an item, by the same rule the backlog uses. */
export function stageOfItem(item: WorkItemDetail): GroomingStage {
  if (!item.readiness.readyForGrooming) return "needs-information";
  return typeof item.estimate === "number" && item.estimate > 0 ? "groomed" : "ready-to-groom";
}

/** The team's points: the brief block's view of ADO, else the live estimate. */
function teamPointsOf(item: WorkItemDetail): number | null {
  if (item.groomingBrief?.teamPoints != null) return item.groomingBrief.teamPoints;
  return typeof item.estimate === "number" && item.estimate > 0 ? item.estimate : null;
}

// ---------------------------------------------------------------------------
// The shared agenda (Add to session / In session)
// ---------------------------------------------------------------------------

export interface AgendaMembership {
  /** True when the item is on the next session's shared agenda. */
  has: (itemId: string) => boolean;
  /** Add the item to the shared agenda, or take it off. */
  toggle: (itemId: string) => void;
  /** True while an add or remove is in flight. */
  isPending: boolean;
  /**
   * `manage-agenda` on the project (Admin, Project Manager; E283). Without
   * it the agenda is read-only: `toggle` does nothing and no Add to session
   * control shows.
   */
  canEdit: boolean;
}

/**
 * The project's shared session agenda, built by people with `manage-agenda`
 * (E283). The backlog's A key, its row action and the pane's header button
 * share this.
 */
export function useAgendaMembership(project: string): AgendaMembership {
  const canEdit = useCan(project, "manage-agenda").allowed;
  const session = useProjectSession(project);
  const add = useAddToAgenda(project);
  const remove = useRemoveFromAgenda(project);
  const batch = session.data?.batch ?? null;
  const ids = new Set((batch?.items ?? []).map((entry) => entry.itemId));
  const isPending = add.isPending || remove.isPending;

  const toggle = (itemId: string) => {
    if (isPending || !canEdit) return;
    if (ids.has(itemId)) {
      remove.mutate(
        batch ? { itemId, batchId: batch.id } : { itemId },
        {
          onSuccess: () => showGroomingToast(`AB#${itemId} taken off the session`, "info"),
          onError: () => showGroomingToast("It could not be taken off the session. Try again.", "info"),
        }
      );
    } else {
      add.mutate([itemId], {
        onSuccess: () => showGroomingToast(`AB#${itemId} added to the session`),
        onError: () => showGroomingToast("It could not be added to the session. Try again.", "info"),
      });
    }
  };

  return { has: (itemId) => ids.has(itemId), toggle, isPending, canEdit };
}

/**
 * The pane header's Add to session / In session toggle (ready-to-groom items
 * only). Read-only without `manage-agenda`: an "In session" chip when the
 * item is on the agenda, else nothing.
 */
export function SessionToggle({ itemId, agenda }: { itemId: string; agenda: AgendaMembership }) {
  const inSession = agenda.has(itemId);
  if (!agenda.canEdit) {
    return inSession ? (
      <Chip tone="ok" testId="session-chip">
        In session
      </Chip>
    ) : null;
  }
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[13px] font-medium transition-colors disabled:opacity-50",
        inSession
          ? "border-green-500/25 bg-green-500/[0.14] text-green-400"
          : "border-[#27272A] bg-[#1A1A1E] text-foreground hover:bg-[#222227]"
      )}
      aria-pressed={inSession}
      disabled={agenda.isPending}
      data-testid="session-toggle"
      title={inSession ? "Take it off the next session's agenda (A)" : "Add it to the next session's agenda (A)"}
      onClick={(event) => {
        event.stopPropagation();
        agenda.toggle(itemId);
      }}
    >
      {inSession ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Plus className="h-3.5 w-3.5" aria-hidden="true" />}
      {inSession ? "In session" : "Add to session"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pieces of the summary
// ---------------------------------------------------------------------------

function Question({ title, children, testId }: { title: string; children: ReactNode; testId: string }) {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <h2 className="font-display text-sm font-semibold leading-snug text-foreground">{title}</h2>
      {children}
    </section>
  );
}

/** The comparable in words: the brief's own sentence, else the first similar past item. */
function comparableSentence(brief: GroomingBrief): string | null {
  const plain = brief.body.plainBrief?.comparableInWords;
  if (plain) return plain;
  const first = brief.calibrated ? brief.body.comparables[0] : undefined;
  if (!first) return null;
  return `About the same size as AB#${first.id} "${first.title}" (${String(first.points)} points).`;
}

/** "How big?": the team's points, Claude's suggestion, or no brief yet. */
function HowBig({ item, brief }: { item: WorkItemDetail; brief: GroomingBrief | null }) {
  const teamPoints = teamPointsOf(item);
  const comparable = brief ? comparableSentence(brief) : null;
  return (
    <div className="glass-card flex flex-col gap-2.5 rounded-[10px] px-4 py-3.5" data-testid="how-big">
      {/* UX-PM-30: the heading, then its source chip on the line under it. */}
      <div className="flex flex-col items-start gap-1.5" data-testid="how-big-header">
        <h2 className="font-display text-sm font-semibold">How big?</h2>
        {teamPoints !== null ? (
          <Chip tone="ok" testId="size-source">
            Team&apos;s points
          </Chip>
        ) : brief ? (
          <Chip tone="none" testId="size-source">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            Claude&apos;s suggestion
          </Chip>
        ) : null}
      </div>
      {teamPoints !== null ? (
        <>
          <PointScale low={teamPoints} high={teamPoints} pick={teamPoints} />
          <p className="text-sm text-muted-foreground" data-testid="team-points">
            The team set {teamPoints} {teamPoints === 1 ? "point" : "points"}. Set in ADO during grooming.
          </p>
        </>
      ) : brief ? (
        <>
          <PointScale low={brief.pointsLow} high={brief.pointsHigh} pick={brief.suggestedPoints} />
          <p className="leading-normal">
            <b>{sizeSentence(brief.suggestedPoints)}</b>{" "}
            <span className="text-muted-foreground">
              {rangeSentence(brief.pointsLow, brief.pointsHigh)}
              {comparable ? ` ${comparable}` : ""}
            </span>
          </p>
          <Chip tone={confidenceTone(brief.confidence)} className="self-start" testId="confidence-chip">
            {confidenceWords(brief.confidence)}
          </Chip>
        </>
      ) : (
        <p className="text-sm leading-normal text-muted-foreground" data-testid="no-brief-yet">
          {NO_BRIEF_TEXT}
        </p>
      )}
    </div>
  );
}

/** "Technical details for the team", collapsed: what changes, size drivers, similar work, engineer notes. */
function TechnicalDetails({ brief }: { brief: GroomingBrief }) {
  const body = brief.body;
  return (
    <details className="group rounded-lg border border-border text-muted-foreground" data-testid="technical-details">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5">
        <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden="true" />
        <span>Technical details for the team</span>
        <span className={cn("ml-auto text-xs", TOOLBOX_TEXT.faint)}>changes, size drivers, similar work</span>
      </summary>
      <div className="space-y-4 border-t border-border px-3 py-3 text-foreground">
        <section className="space-y-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">In short</h3>
          <p className="whitespace-pre-line text-sm">{body.plainSummary}</p>
        </section>
        {body.whatChanges.length > 0 && (
          <section className="space-y-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">What changes</h3>
            <TextList items={body.whatChanges} />
          </section>
        )}
        {body.sizeDrivers.length > 0 && (
          <section className="space-y-1.5" data-testid="technical-size-drivers">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Size drivers</h3>
            <SizeDrivers drivers={body.sizeDrivers} />
            {body.splitSuggestion && (
              <p className="text-sm">
                <span className="font-medium">Consider splitting:</span> {body.splitSuggestion}
              </p>
            )}
          </section>
        )}
        {brief.calibrated && body.comparables.length > 0 && (
          <section className="space-y-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Similar past work</h3>
            <ul className="space-y-1 text-sm">
              {body.comparables.map((c) => (
                <li key={c.id}>
                  <span className="font-mono text-xs text-muted-foreground">#{c.id}</span>{" "}
                  <span className="font-medium">{c.title}</span>{" "}
                  <span className="text-muted-foreground">({c.points} pts)</span> — {c.why}
                </li>
              ))}
            </ul>
          </section>
        )}
        <EngineerNotesBody brief={brief} listTextClassName="text-[13px]" />
      </div>
    </details>
  );
}

/** Start one-item grooming in the app and re-read the item when Claude finishes. */
function useGroomOne(project: string, item: WorkItemDetail) {
  const { runner } = useRunnerBridge();
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ status: "idle" | "running" | "done" | "failed"; message?: string }>({
    status: "idle",
  });

  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (event.type !== "item-finished" || event.itemId !== item.ref.id) return;
      if (event.outcome === "failed") {
        setState({ status: "failed", message: event.message ?? "Claude could not finish the brief." });
      } else {
        setState({ status: "done" });
      }
      void queryClient.invalidateQueries({
        queryKey: workItemKeys.detail(item.ref.tracker, item.ref.container, item.ref.id),
      });
    },
    [item.ref, queryClient]
  );
  useRunnerEvents(runner, onEvent);

  const start = () => {
    if (!runner) return;
    setState({ status: "running" });
    void runner.groomOne(project, item.ref.id).then(
      (result) => {
        if (!result.ok) setState({ status: "failed", message: result.message });
      },
      () => {
        setState({ status: "failed", message: "SpecTree Toolbox could not start the run." });
      }
    );
  };

  return { runner, state, start };
}

/**
 * "Groom this item" in the app, or the slash command in a browser. In a
 * contract-3 app (epic PI-SPEC-SW-E284) the button adds the item to the run
 * queue, and while the item is queued or running its queue badge replaces
 * the button, so it cannot be added twice. Since epic PI-SPEC-SW-E290
 * (feature PI-SPEC-SW-1482, UX-PM-04, frame B4) a single item waiting in Up
 * next shows the compact "AB#… is next in line" card instead of the badge.
 */
function GroomAction({ project, item, label }: { project: string; item: WorkItemDetail; label: string }) {
  const { support } = useRunnerBridge();
  const { runner, state, start } = useGroomOne(project, item);
  const queryClient = useQueryClient();
  const queue = useEnqueueItem(project, item.ref.id, () => {
    void queryClient.invalidateQueries({
      queryKey: workItemKeys.detail(item.ref.tracker, item.ref.container, item.ref.id),
    });
  });

  if (queue.available) {
    return (
      <div className="space-y-2" data-testid="groom-action">
        {queue.active?.kind === "queued" && showsNextInLineCard(queue.active.entry) ? (
          <NextInLineCard entryId={queue.active.entry.id} project={project} title={item.title} size="compact" />
        ) : queue.active ? (
          <QueueBadge status={queue.active} />
        ) : (
          <Button
            type="button"
            size="sm"
            className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={queue.pending}
            onClick={() => {
              void queue.enqueue();
            }}
          >
            {label}
          </Button>
        )}
        {queue.error && (
          <p className="text-sm text-destructive" role="alert">
            {queue.error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="groom-action">
      <Button
        type="button"
        size="sm"
        className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={!runner || state.status === "running"}
        onClick={start}
      >
        {state.status === "running" ? "Grooming…" : label}
      </Button>
      {state.status === "running" && (
        <p className="text-sm text-muted-foreground" role="status">
          Claude is reading the item and the code. The brief appears here when it finishes.
        </p>
      )}
      {state.status === "failed" && (
        <p className="text-sm text-destructive" role="alert">
          {state.message}
        </p>
      )}
      <RunnerFallback
        support={support}
        command={{
          label: "Or groom this one item in Claude Code",
          command: groomCommand(item.ref.id),
          helperText: "Run this in Claude Code inside the code repository. The brief is saved in SpecTree.",
        }}
      />
    </div>
  );
}

function StaleBanner({ project, item }: { project: string; item: WorkItemDetail }) {
  const mayGroom = useCan(project, "groom-one").allowed;
  return (
    <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" role="status" data-testid="stale-banner">
      <p className="flex items-start gap-2 text-sm text-amber-300">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        The item changed in ADO since this brief was written.
      </p>
      {mayGroom && <GroomAction project={project} item={item} label="Groom it again" />}
    </div>
  );
}

/** The summary under question headings; falls back to the E277 fields when the brief predates plainBrief. */
function BriefSections({ project, item, block, brief }: { project: string; item: WorkItemDetail; block: WorkItemGroomingBriefBlock; brief: GroomingBrief }) {
  const plain = brief.body.plainBrief;
  const askTheTeam = plain?.askTheTeam.length ? plain.askTheTeam : brief.body.questions;

  return (
    <div className="flex flex-col gap-4 [&>*+*]:border-t [&>*+*]:border-[#18181B] [&>*+*]:pt-4" data-testid="brief-summary">
      {block.stale && <StaleBanner project={project} item={item} />}
      <Question title="What is it?" testId="q-what-is-it">
        <p className="text-sm leading-relaxed">{plain?.whatIsIt ?? brief.body.plainSummary}</p>
      </Question>
      <Question title="Who notices?" testId="q-who-notices">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {plain?.whoNotices ?? "This brief was written before it said who notices. Groom the item again to add it."}
        </p>
      </Question>
      <Question title="What gets touched?" testId="q-what-gets-touched">
        {plain && plain.areasTouched.length > 0 ? (
          <AreaList areas={plain.areasTouched} />
        ) : brief.body.whatChanges.length > 0 ? (
          <TextList items={brief.body.whatChanges} />
        ) : (
          <p className="text-sm text-muted-foreground">Not covered by this brief.</p>
        )}
      </Question>
      <Question title="Worth knowing" testId="q-worth-knowing">
        {plain && plain.worthKnowing.length > 0 ? (
          <TextList items={plain.worthKnowing} />
        ) : (
          <p className="text-sm text-muted-foreground">Nothing extra.</p>
        )}
      </Question>
      <Question title="Ask the team" testId="q-ask-the-team">
        {askTheTeam.length > 0 ? <TextList items={askTheTeam} /> : <p className="text-sm text-muted-foreground">No open questions.</p>}
      </Question>
      <TechnicalDetails brief={brief} />
    </div>
  );
}

/**
 * "3 of 4" for the "changed since you asked" note: the live checklist count,
 * or null when the readiness read has no criteria (only the missing rules).
 */
function inPlaceCount(readiness: Pick<ReadinessLevelResult, "missing" | "criteria">): string | null {
  if (!readiness.criteria || readiness.criteria.length === 0) return null;
  const rows = checklistRows(readiness);
  return `${String(rows.filter((row) => row.present).length)} of ${String(rows.length)}`;
}

/**
 * The amber note above the checklist when the item changed after its author
 * was asked (epic PI-SPEC-SW-E292, UX-PM-06, frame D4): the change date, whom
 * to check for an answer, and the live "k of n in place" count. Text only.
 */
function ChangedSinceAskNote({ ask, item }: { ask: LatestAuthorQuestionAsk; item: WorkItemDetail }) {
  const date = shortDate(ask.itemChangedAt ?? item.changedDate);
  const who = firstName(ask.mentionedName ?? item.createdBy?.displayName ?? null);
  const count = inPlaceCount(item.readiness.grooming);
  return (
    <div
      className="flex items-start gap-2.5 rounded-[10px] border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.08)] px-3.5 py-3"
      role="status"
      data-testid="changed-since-ask"
    >
      <History className="mt-0.5 h-4 w-4 flex-none text-[#FBBF24]" aria-hidden="true" />
      <p className="text-[13px] leading-normal text-[#FDE68A]">
        The item changed{date ? ` on ${date}` : ""}, after you asked. Check whether {who} answered; the checklist below is
        read again
        {count ? (
          <>
            : <b className="font-semibold">{count}</b> in place now.
          </>
        ) : (
          "."
        )}
      </p>
    </div>
  );
}

/**
 * The navigation state value or URL hash (`#author-questions`) that asks the
 * pane to scroll to the questions for the author and focus the first box
 * (E292): set by the Backlog's Ask the author and Check the reply. The one
 * name the Backlog and the pane share (the Backlog imports it).
 */
export const AUTHOR_QUESTIONS_FOCUS = "author-questions";

/** True when router state `{ focus: "author-questions" }` or the hash asks for the questions. Pure. */
export function asksForAuthorQuestions(location: { state?: unknown; hash?: string }): boolean {
  if (location.hash === `#${AUTHOR_QUESTIONS_FOCUS}`) return true;
  const state = location.state;
  return typeof state === "object" && state !== null && (state as { focus?: unknown }).focus === AUTHOR_QUESTIONS_FOCUS;
}

/**
 * The line under the pane header for an item below the grooming bar (E292,
 * UX-PM-06, frames D3/D4), in the slot the brief's state bar takes for an
 * item with a brief: "Needs information · Written by Chris Patel on Oct 9 ·
 * not asked yet" (or "· asked Oct 14"). Tracker names are rendered as text.
 */
export function AuthorStateBar({ item }: { item: WorkItemDetail }) {
  const askQuery = useAuthorQuestionsLatestAsk(item.ref.container, item.ref.id);
  const ask = askQuery.data ?? null;
  const author = item.createdBy?.displayName;
  const written = shortDate(item.createdDate);
  const asked = ask ? shortDate(ask.createdAt) : null;
  const parts = [
    author ? `Written by ${author}${written ? ` on ${written}` : ""}` : `Written${written ? ` on ${written}` : " by an unknown author"}`,
  ];
  // A failed read says nothing about whether the author was asked: never "not asked yet" then.
  if (!askQuery.isLoading && !(askQuery.isError && askQuery.data === undefined)) {
    parts.push(ask ? `asked${asked ? ` ${asked}` : ""}` : "not asked yet");
  }
  return (
    <div
      className="flex flex-none flex-wrap items-center gap-2.5 border-b border-[#1F1F23] bg-[#0E0E10] px-5 py-3"
      data-testid="author-state-bar"
    >
      <Chip tone="warn">Needs information</Chip>
      <span className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="author-state-line">
        {parts.join(" · ")}
      </span>
    </div>
  );
}

/** Below the grooming standard: the checklist, questions for the author, and the Formulator. */
function BelowStandard({
  project,
  item,
  focusQuestions = false,
}: {
  project: string;
  item: WorkItemDetail;
  focusQuestions?: boolean;
}) {
  const mayFormulate = useCanAnywhere("formulator").allowed;
  // E292: the latest ask rides on the item read (no request of its own).
  const ask = useAuthorQuestionsLatestAsk(item.ref.container, item.ref.id).data ?? null;
  return (
    <div className="flex flex-col gap-4" data-testid="below-bar">
      {ask?.changedSinceAsk && <ChangedSinceAskNote ask={ask} item={item} />}
      <ReadinessChecklist readiness={item.readiness.grooming} />
      <AuthorQuestionsCard key={`${project}/${item.ref.id}`} project={project} item={item} focusRequested={focusQuestions} />
      {mayFormulate && (
        <div className="glass-card flex flex-col gap-1.5 rounded-[10px] px-4 py-3" data-testid="fix-it-yourself">
          <span className="font-medium">Or fix it yourself</span>
          <span className="text-[13px] leading-relaxed text-muted-foreground">
            The ADO Formulator asks a few questions and rewrites the item to meet the standard.
          </span>
          {item.formulatorPath && (
            <Link to={item.formulatorPath} className="self-start text-sm text-link hover:underline">
              Open in ADO Formulator
            </Link>
          )}
        </div>
      )}
      <span className={cn("text-[13px]", TOOLBOX_TEXT.faint)}>No size suggestion until the item meets the standard.</span>
    </div>
  );
}

/** The item's body: below the standard, or How big plus the brief sections. */
function PaneBody({ project, item, focusQuestions = false }: { project: string; item: WorkItemDetail; focusQuestions?: boolean }) {
  const mayGroom = useCan(project, "groom-one").allowed;
  if (!item.readiness.readyForGrooming) return <BelowStandard project={project} item={item} focusQuestions={focusQuestions} />;
  const block = item.groomingBrief;
  if (block === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="brief-unavailable">
        The brief could not be loaded. Try again in a moment.
      </p>
    );
  }
  const brief = block?.brief ?? null;
  const stage = stageOfItem(item);
  return (
    <div className="flex flex-col gap-4">
      <HowBig item={item} brief={brief} />
      {brief && block ? (
        <BriefSections project={project} item={item} block={block} brief={brief} />
      ) : stage === "ready-to-groom" && mayGroom ? (
        <div data-testid="no-brief">
          <GroomAction project={project} item={item} label="Groom this item" />
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The pane
// ---------------------------------------------------------------------------

/** Every Tailwind class of the pane; the width alone is a runtime value (`style`). */
const PANE_CLASSES: Record<ReadingPaneVariant, string> = {
  side: "relative flex-none border-l border-border bg-[#0B0B0D]",
  over: "absolute inset-y-0 right-0 z-10 border-l border-[#27272A] bg-[#0B0B0D] shadow-[-24px_0_60px_rgba(0,0,0,0.5)]",
};

/**
 * The pane's left edge (frame 1b): a focusable vertical separator with the
 * 6 x 40 px grip. Dragging it left widens the pane; ← and → move it 24 px.
 * `onResize(px, false)` follows the drag; `onResize(px, true)` ends it (the
 * pointer is released, or an arrow key moved it), when the width is saved.
 */
function PaneResizeHandle({
  width,
  maxWidth,
  onResize,
}: {
  width: number;
  maxWidth?: number | undefined;
  onResize: (px: number, done: boolean) => void;
}) {
  const drag = useRef<{ startX: number; startWidth: number; last: number } | null>(null);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    drag.current = { startX: event.clientX, startWidth: width, last: width };
    if (typeof event.currentTarget.setPointerCapture === "function")
      event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) return;
    current.last = current.startWidth + (current.startX - event.clientX);
    onResize(current.last, false);
  };
  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) return;
    drag.current = null;
    if (
      typeof event.currentTarget.releasePointerCapture === "function" &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onResize(current.last, true);
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === "ArrowLeft"
        ? PANE_WIDTH_STEP
        : event.key === "ArrowRight"
          ? -PANE_WIDTH_STEP
          : 0;
    if (step === 0) return;
    event.preventDefault();
    // Keep the backlog's own arrow keys (↓/↑ move the list) out of it.
    event.stopPropagation();
    onResize(width + step, true);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the reading pane"
      aria-valuenow={Math.round(width)}
      aria-valuemin={PANE_WIDTH_MIN}
      aria-valuemax={maxWidth}
      tabIndex={0}
      title="Drag to resize"
      className="group/grip absolute -left-[3px] inset-y-0 z-20 flex w-[6px] cursor-col-resize touch-none items-center justify-center focus:outline-none"
      data-testid="pane-resize-handle"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    >
      <span
        className="h-10 w-[6px] rounded-[3px] bg-[#3F3F46] transition-colors group-hover/grip:bg-[#52525B] group-focus-visible/grip:bg-primary"
        aria-hidden="true"
      />
    </div>
  );
}

/**
 * The tallest the pane's sticky column may be: the height of the page's
 * scroll area (the Toolbox main area, under the title bar and, while the app
 * reports an update, E288's update band), so its bottom never falls below the
 * window. Null outside such an area (a plain browser page scrolls the window),
 * where the viewport-based class applies.
 */
function useScrollAreaHeight(ref: RefObject<HTMLElement | null>): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    let area: HTMLElement | null = element.parentElement;
    while (area && area !== document.body) {
      const overflowY = window.getComputedStyle(area).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") break;
      area = area.parentElement;
    }
    if (!area || area === document.body) return undefined;
    const scrollArea = area;
    const measure = () => {
      setHeight(scrollArea.clientHeight > 0 ? scrollArea.clientHeight : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(scrollArea);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return height;
}

export interface ReadingPaneProps {
  project: string;
  itemId: string;
  /** The item's place in the whole backlog (1 = top), when the backlog read has it. */
  position?: number | null;
  variant: ReadingPaneVariant;
  agenda: AgendaMembership;
  /** E283: who is preparing the item in a running batch (from its backlog row); informational only. */
  preparing?: GroomingPreparing | null;
  onClose: () => void;
  /** The pane's width in px (E289, UX-PM-18); 540 when the page does not set one. */
  width?: number;
  /** The widest the pane may get, for the resize handle's aria-valuemax. */
  maxWidth?: number | undefined;
  /** Resize from the left edge; without it the pane has no resize handle. `done` ends a drag or a key press. */
  onResize?: (px: number, done: boolean) => void;
  /** True while the pane shows full width with the list hidden. */
  expanded?: boolean;
  /** The header's expand / Back to the list button; without it the button is not shown. */
  onToggleExpand?: () => void;
}

/**
 * The item's place in the backlog when its read from ADO failed (UX-PM-21):
 * the position comes from the backlog read, so it still shows, once, where
 * the facts line would be. Nothing without a position.
 */
function BacklogPositionLine({ position }: { position: number | null }) {
  if (position === null) return null;
  return (
    <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="item-facts-line">
      #{String(position)} in the backlog
    </span>
  );
}

/** One item's summary beside the list or over the board. */
export function ReadingPane({
  project,
  itemId,
  position = null,
  variant,
  agenda,
  preparing = null,
  onClose,
  width = PANE_WIDTH_DEFAULT,
  maxWidth,
  onResize,
  expanded = false,
  onToggleExpand,
}: ReadingPaneProps) {
  const itemQuery = useWorkItem("ado", project, itemId);
  const item = itemQuery.data;
  const stage = item ? stageOfItem(item) : null;
  const columnRef = useRef<HTMLDivElement>(null);
  const areaHeight = useScrollAreaHeight(columnRef);
  // E292: opened from the Backlog's Ask the author / Check the reply.
  const focusQuestions = asksForAuthorQuestions(useLocation());

  return (
    <aside
      className={PANE_CLASSES[variant]}
      // The one runtime value: the remembered width, or the whole split when expanded.
      style={{ width: expanded ? "100%" : width }}
      data-testid="reading-pane"
      data-variant={variant}
      data-expanded={expanded ? "true" : undefined}
      aria-label={`Summary of AB#${itemId}`}
    >
      {onResize && !expanded && (
        <PaneResizeHandle width={width} maxWidth={maxWidth} onResize={onResize} />
      )}
      <div
        ref={columnRef}
        className="sticky top-0 flex max-h-[calc(100vh-2rem)] flex-col"
        style={areaHeight !== null ? { maxHeight: areaHeight } : undefined}
      >
        <div className="flex min-h-[46px] flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          {/* UX-PM-21 (PI-SPEC-SW-1457): the header carries only the ID; type and backlog place show once, in the body. */}
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="item-id-line">
            AB#{itemId}
          </span>
          {stage === "ready-to-groom" && <SessionToggle itemId={itemId} agenda={agenda} />}
          {item && (
            <a
              href={item.ref.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-[26px] items-center gap-1.5 rounded-lg px-2 text-[13px] font-medium text-muted-foreground hover:bg-[#141417] hover:text-foreground"
              aria-label="Open in ADO"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              ADO
            </a>
          )}
          {onToggleExpand && (
            <button
              type="button"
              className="inline-flex h-[26px] items-center rounded-lg px-1.5 text-muted-foreground hover:bg-[#141417] hover:text-foreground"
              aria-label={expanded ? "Back to the list" : "Show the brief full width"}
              title={expanded ? "Back to the list" : "Show the brief full width"}
              aria-pressed={expanded}
              data-testid="pane-expand"
              onClick={onToggleExpand}
            >
              {expanded ? (
                <Minimize2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Maximize2 className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
          <button
            type="button"
            className="inline-flex h-[26px] items-center rounded-lg px-1.5 text-muted-foreground hover:bg-[#141417] hover:text-foreground"
            aria-label="Close the summary (Esc)"
            title="Close (Esc)"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* UX-PM-02 (frame 1b): the brief's state, Hold back and Post to ADO, outside the scroll so they stay in view. */}
        {item && !item.readiness.readyForGrooming ? (
          // E292 (frames D3/D4): who wrote a below-bar item and whether they were asked, in the same slot.
          // Live readiness wins: an item that fell below the bar keeps any old brief, which is out of date.
          <AuthorStateBar key={`author-bar-${itemId}`} item={item} />
        ) : item?.groomingBrief?.brief ? (
          <BriefStateBar key={`bar-${itemId}`} project={project} itemId={itemId} variant="pane" />
        ) : null}

        {/* Full width keeps the brief in a readable column: the side padding centres it at 880 px. */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-7 pt-5",
            expanded && "px-[max(1.5rem,calc((100%-880px)/2))]"
          )}
          key={itemId}
        >
          {itemQuery.isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center" role="status" aria-live="polite">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="sr-only">Reading the item from ADO…</span>
            </div>
          ) : itemQuery.isError || !item ? (
            <>
              <BacklogPositionLine position={position} />
              {/* B6 (UX-PM-37): the cause, Try again and what was already tried; "not found" keeps its sentence. */}
              <AdoReadErrorCard query={itemQuery} subject="item" project={project} itemId={itemId} compact testId="item-read-error" />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                  {stage && <StageChip stage={stage} />}
                  <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="item-facts-line">
                    {item.typeName}
                    {position !== null ? ` · #${String(position)} in the backlog` : ""}
                  </span>
                </div>
                <h1 className="font-display text-lg font-semibold leading-snug">{item.title}</h1>
                <PreparingMarker preparing={preparing} project={project} itemId={itemId} className="text-xs" />
              </div>
              <PaneBody project={project} item={item} focusQuestions={focusQuestions} />
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
